import React, {useEffect, useMemo, useState} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {
    FiDatabase,
    FiClipboard,
    FiActivity,
    FiHelpCircle,
    FiZap,
    FiUser,
    FiUsers,
    FiX,
    FiLock,
    FiMessageCircle,
    FiMessageSquare,
    FiBarChart2,
    FiGitBranch,
} from "react-icons/fi";
import {BsGraphUp} from "react-icons/bs";
import {FiChevronDown} from "react-icons/fi";
import useAuthRole from "../../hooks/useAuthRole";
import {getDjangoAdminUrl} from "../../services/api";
import "./sidebar.css";

export default function Sidebar({mobileOpen, onCloseMobile}) {
    const location = useLocation();
    const {role, isPlatformAdmin} = useAuthRole();
    const homePath = role === "student" ? "/exampage" : "/dashboard";
    const homeTitle = role === "student" ? "پنل دانشجو" : "داشبورد";

    const teacherMenuItems = [
        {icon: <FiGitBranch size={18}/>, title: "منابع و گراف دانش", to: "/manageresources"},
        {icon: <FiHelpCircle size={18}/>, title: "طراحی هوشمند سؤال", to: "/question-engine"},
        ...(isPlatformAdmin ? [{icon: <FiActivity size={18}/>, title: "لاگ‌های LLM", to: "/llm-logs"}] : []),

        {icon: <FiDatabase size={18}/>, title: "بانک سوالات", to: "/questionBank"},
        {icon: <FiUsers size={18}/>, title: "مجموعه‌ها", to: "/organizations"},
    ];
    const studentMenuItems = [
        {icon: <FiClipboard size={18}/>, title: "آزمون‌های من", to: "/exampage", end: true},
        {icon: <FiBarChart2 size={18}/>, title: "نتایج و نمره‌ها", to: "/exampage/results"},
        {icon: <FiMessageSquare size={18}/>, title: "بازخورد و اعتراض‌ها", to: "/exampage/requests"},
    ];
    const menuItems = [
        ...(role === "teacher" ? [...teacherMenuItems, {
            icon: <FiClipboard size={18}/>, title: "آزمون‌ها", to: "/exampage", end: true
        }] : studentMenuItems),
        ...(isPlatformAdmin ? [
            {icon: <FiMessageCircle size={18}/>, title: "دستیار یادگیری", to: "/learning-agent"},
            {icon: <FiZap size={18}/>, title: "دستیار معلم", to: "/teacher-agent"},
            {icon: <FiBarChart2 size={18}/>, title: "وضعیت یادگیرندگان", to: "/learner-state"},
        ] : []),
    ];

    const isProfileRoute = location.pathname.startsWith("/profilepage");

    const currentProfileTab = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("tab") || "profile";
    }, [location.search]);

    const [profileOpen, setProfileOpen] = useState(false);

    // وقتی کاربر وارد مسیر پروفایل شد، زیرمنو خودکار باز شود
    useEffect(() => {
        if (isProfileRoute) setProfileOpen(true);
    }, [isProfileRoute]);

    const closeMobileIfNeeded = () => {
        if (onCloseMobile) onCloseMobile();
    };

    return (
        <aside className={`app-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
            {/* هدر کوچک فقط موبایل */}
            <div className="app-sidebar-mobile-header">
                <NavLink to={homePath} className="app-sidebar-title" onClick={closeMobileIfNeeded}>
          <span className="app-sidebar-title-icon">
            <BsGraphUp/>
          </span>
                    <span>{homeTitle}</span>
                </NavLink>

                <button className="app-sidebar-close-btn" onClick={closeMobileIfNeeded} aria-label="بستن منوی اصلی">
                    <FiX/>
                </button>
            </div>

            {/* تیتر ثابت (دسکتاپ) */}
            <NavLink
                to={homePath}
                className={({isActive}) => `app-sidebar-section-title ${isActive ? "active" : ""}`}
            >
        <span className="app-sidebar-title-icon">
          <BsGraphUp/>
        </span>
                <span>{homeTitle}</span>
            </NavLink>

            <nav className="app-sidebar-menu">
                {menuItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({isActive}) => `app-menu-item ${isActive ? "active" : ""}`}
                        onClick={closeMobileIfNeeded}
                    >
                        <span className="app-menu-icon">{item.icon}</span>
                        <span className="app-menu-text">{item.title}</span>
                    </NavLink>
                ))}
                {isPlatformAdmin && (
                    <a className="app-menu-item" href={getDjangoAdminUrl()} onClick={closeMobileIfNeeded}>
                        <span className="app-menu-icon"><FiLock size={18}/></span>
                        <span className="app-menu-text">مدیریت سامانه</span>
                    </a>
                )}

                {/* ===== پروفایل (مثل بقیه آیتم‌ها) + فلش داخل همان ردیف ===== */}
                <div className="app-profile-group">
                    {/* ردیف اصلی پروفایل: لینک + فلش که هاورش یکیه */}
                    <div className={`app-menu-item app-profile-parent ${isProfileRoute ? "active-parent" : ""}`}>
                        <NavLink
                            to="/profilepage?tab=profile"
                            className={() =>
                                `app-profile-parent-link ${
                                    isProfileRoute && currentProfileTab === "profile" ? "active" : ""
                                }`
                            }
                            onClick={closeMobileIfNeeded}
                        >
              <span className="app-menu-icon">
                <FiUser size={18}/>
              </span>
                            <span className="app-menu-text">پروفایل</span>
                        </NavLink>

                        {/* فلش داخل همون ردیف */}
                        <button
                            type="button"
                            className="app-profile-caret"
                            onClick={() => setProfileOpen((p) => !p)}
                            aria-label={profileOpen ? "بستن زیرمنوی پروفایل" : "باز کردن زیرمنوی پروفایل"}
                            aria-expanded={profileOpen}
                        >
                            <FiChevronDown className={`caret-icon ${profileOpen ? "rotate" : ""}`}/>
                        </button>
                    </div>

                    {/* زیرمنوها: دقیقا با کلاس app-menu-item مثل بقیه */}
                    <div className={`app-profile-submenu ${profileOpen ? "open" : ""}`}>
                        <NavLink
                            to="/profilepage?tab=security"
                            className={() =>
                                `app-menu-item app-profile-subitem ${
                                    isProfileRoute && currentProfileTab === "security" ? "active" : ""
                                }`
                            }
                            onClick={closeMobileIfNeeded}
                        >
              <span className="app-menu-icon">
                <FiLock size={18}/>
              </span>
                            <span className="app-menu-text">امنیت</span>
                        </NavLink>
                    </div>
                </div>
            </nav>
        </aside>
    );
}
